import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { Mail, Lock, User, Loader2, ArrowLeft, Eye, EyeOff } from 'lucide-react';

// URLs das ilustrações originais
const LOGO_URL = "https://customer-assets.emergentagent.com/job_pet-photo-storage/artifacts/343ueuet_logo.png";

const AuthPage = () => {
  const { login, register, user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  
  // Se veio de "cadastrar pet", mostra tela de cadastro direto
  const action = searchParams.get('action');
  const [isLogin, setIsLogin] = useState(false); // Começa com cadastro
  
  const [showPassword, setShowPassword] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);

  const [loginData, setLoginData] = useState({ email: '', password: '' });
  const [registerData, setRegisterData] = useState({ nome: '', email: '', password: '', confirmPassword: '' });

  // Atualiza isLogin baseado no action parameter
  useEffect(() => {
    if (action === 'cadastrar') {
      setIsLogin(false); // Mostra cadastro
    } else {
      setIsLogin(true); // Mostra login
    }
  }, [action]);

  // Redirect if already logged in
  useEffect(() => {
    if (user) {
      // Se veio de cadastrar pet, redireciona para lá
      if (action === 'cadastrar') {
        navigate('/cadastrar-pet');
      } else {
        navigate('/');
      }
    }
  }, [user, navigate, action]);

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!loginData.email || !loginData.password) {
      toast.error('Por favor, preencha todos os campos');
      return;
    }

    setLoading(true);
    try {
      await login(loginData.email, loginData.password);
      toast.success('Login realizado com sucesso!');
      // Se veio de cadastrar pet, redireciona para lá
      if (action === 'cadastrar') {
        navigate('/cadastrar-pet');
      } else {
        navigate('/');
      }
    } catch (error) {
      console.error('Login error:', error);
      const message = error.response?.data?.detail || 'Erro ao fazer login';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    if (!registerData.nome || !registerData.email || !registerData.password || !registerData.confirmPassword) {
      toast.error('Por favor, preencha todos os campos');
      return;
    }

    if (registerData.password !== registerData.confirmPassword) {
      toast.error('As senhas não coincidem');
      return;
    }

    if (registerData.password.length < 6) {
      toast.error('A senha deve ter pelo menos 6 caracteres');
      return;
    }

    if (!acceptedTerms) {
      toast.error('Você precisa aceitar os Termos de Uso para criar uma conta');
      return;
    }

    setLoading(true);
    try {
      await register(registerData.email, registerData.password, registerData.nome);
      toast.success('Conta criada com sucesso!');
      // Se veio de cadastrar pet, redireciona para lá
      if (action === 'cadastrar') {
        navigate('/cadastrar-pet');
      } else {
        navigate('/');
      }
    } catch (error) {
      console.error('Register error:', error);
      const message = error.response?.data?.detail || 'Erro ao criar conta';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F5D79C] flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Back to Home */}
        <Link 
          to="/" 
          className="inline-flex items-center gap-2 text-[#5D4A3A] hover:text-[#7A5B3E] mb-6 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Voltar para o início
        </Link>

        {/* Logo Container - usando imagem original */}
        <div className="flex flex-col items-center mb-8">
          <div className="logo-container w-40 h-40 flex items-center justify-center mb-4 p-3">
            <img 
              src={LOGO_URL} 
              alt="Miaucaomigo Logo" 
              className="w-full h-full object-contain"
            />
          </div>
          <h1 className="text-3xl font-display font-semibold text-[#5D4A3A]">Miaucaomigo</h1>
        </div>

        {/* Form Card */}
        <div className="space-y-4">
          {isLogin ? (
            /* Login Form */
            <form onSubmit={handleLogin} className="space-y-4">
              <div className="relative">
                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#7A5B3E]" />
                <Input
                  type="email"
                  value={loginData.email}
                  onChange={(e) => setLoginData({ ...loginData, email: e.target.value })}
                  placeholder="E-mail"
                  className="input-original pl-12 h-14 text-base"
                  data-testid="login-email"
                />
              </div>

              <div className="relative">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#7A5B3E]" />
                <Input
                  type={showPassword ? "text" : "password"}
                  value={loginData.password}
                  onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                  placeholder="Senha"
                  className="input-original pl-12 pr-12 h-14 text-base"
                  data-testid="login-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-[#7A5B3E] hover:text-[#5D4A3A]"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>

              <div className="text-right">
                <button type="button" className="text-sm text-[#7A5B3E] hover:text-[#5D4A3A]">
                  Esqueci a senha
                </button>
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full btn-primary h-14 text-lg font-semibold uppercase tracking-wide"
                data-testid="login-submit"
              >
                {loading ? (
                  <><Loader2 className="w-5 h-5 mr-2 animate-spin" /> Entrando...</>
                ) : (
                  'ENTRAR'
                )}
              </Button>

              <p className="text-center text-[#5D4A3A]">
                Não tem conta?{' '}
                <button 
                  type="button" 
                  onClick={() => setIsLogin(false)}
                  className="font-semibold text-[#6B5344] hover:underline"
                >
                  Cadastre-se
                </button>
              </p>
            </form>
          ) : (
            /* Register Form */
            <form onSubmit={handleRegister} className="space-y-4">
              <div className="relative">
                <User className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#7A5B3E]" />
                <Input
                  type="text"
                  value={registerData.nome}
                  onChange={(e) => setRegisterData({ ...registerData, nome: e.target.value })}
                  placeholder="Nome completo"
                  className="input-original pl-12 h-14 text-base"
                  data-testid="register-nome"
                />
              </div>

              <div className="relative">
                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#7A5B3E]" />
                <Input
                  type="email"
                  value={registerData.email}
                  onChange={(e) => setRegisterData({ ...registerData, email: e.target.value })}
                  placeholder="E-mail"
                  className="input-original pl-12 h-14 text-base"
                  data-testid="register-email"
                />
              </div>

              <div className="relative">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#7A5B3E]" />
                <Input
                  type={showPassword ? "text" : "password"}
                  value={registerData.password}
                  onChange={(e) => setRegisterData({ ...registerData, password: e.target.value })}
                  placeholder="Senha (mín. 6 caracteres)"
                  className="input-original pl-12 pr-12 h-14 text-base"
                  data-testid="register-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-[#7A5B3E] hover:text-[#5D4A3A]"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>

              <div className="relative">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#7A5B3E]" />
                <Input
                  type="password"
                  value={registerData.confirmPassword}
                  onChange={(e) => setRegisterData({ ...registerData, confirmPassword: e.target.value })}
                  placeholder="Confirmar senha"
                  className="input-original pl-12 h-14 text-base"
                  data-testid="register-confirm"
                />
              </div>

              {/* Terms Checkbox */}
              <div className="flex items-start gap-3 bg-[#FBE4AD] p-4 rounded-2xl">
                <Checkbox
                  id="terms"
                  checked={acceptedTerms}
                  onCheckedChange={(checked) => setAcceptedTerms(checked)}
                  className="mt-1 border-[#7A5B3E] data-[state=checked]:bg-[#6B5344] data-[state=checked]:border-[#6B5344]"
                  data-testid="accept-terms"
                />
                <label htmlFor="terms" className="text-sm text-[#5D4A3A] cursor-pointer">
                  Li e aceito os{' '}
                  <Link 
                    to="/termos" 
                    className="font-semibold text-[#6B5344] hover:underline"
                    target="_blank"
                  >
                    Termos e Privacidade
                  </Link>
                </label>
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full btn-primary h-14 text-lg font-semibold uppercase tracking-wide"
                data-testid="register-submit"
              >
                {loading ? (
                  <><Loader2 className="w-5 h-5 mr-2 animate-spin" /> Criando...</>
                ) : (
                  'CRIAR CONTA'
                )}
              </Button>

              <p className="text-center text-[#5D4A3A]">
                Já tem conta?{' '}
                <button 
                  type="button" 
                  onClick={() => setIsLogin(true)}
                  className="font-semibold text-[#6B5344] hover:underline"
                >
                  Entrar
                </button>
              </p>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default AuthPage;
